import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import { useTokens } from "../TokenContext";
import { toast } from "sonner";
import QuickReplyChips from "../components/QuickReplyChips";
import {
  Loader2, Shuffle, Send, Check, X, Trophy, Coins, ChevronLeft,
  Upload, AlertTriangle, ListChecks, Users, History as HistoryIcon, Info,
  Smartphone, Monitor, Clock,
} from "lucide-react";

/**
 * V2 — International Friendlies hub.
 *
 *   /v2/friendlies   →   tabs:  Play | Players | Standings | History
 *
 * Backend: /api/friendlies/* (see /app/backend/friendlies.py).
 *
 * Match flow is rendered inline as a "submit-your-score" panel that appears
 * the moment the player has an in-progress friendly. After submission the
 * player can immediately reveal a new team (gate-cleared from the server).
 */
export default function FriendliesV2() {
  const { user } = useAuth();
  const { reload: reloadTokens } = useTokens() || {};
  const { t } = useT();
  const { pool: poolParam } = useParams();
  const pool = (poolParam === "console" || poolParam === "mobile") ? poolParam : null;
  const [tab, setTab] = useState("play");
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/friendlies/me");
      setMe(data);
    } catch {
      setMe({ error: true });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  // Light polling so tab-strip unread badges update while idle.
  useEffect(() => {
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [refresh]);

  if (!me) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-[#00FF87]" />
    </div>
  );
  if (me.error) return (
    <div className="text-center text-sm text-[#71717A] py-20">Friendlies unavailable. Try again later.</div>
  );

  // Determine the active pool for this view:
  //   1. URL param wins (?/v2/friendlies/console or /mobile)
  //   2. Otherwise the player's currently-revealed pool
  //   3. Otherwise their account platform
  const teamPool = me.current_team ? (me.current_pool || "mobile") : null;
  const activePool = pool || teamPool || me.platform || "mobile";
  // Block actions if the player has a team in a DIFFERENT pool already
  const lockedToOtherPool = !!(me.current_team && teamPool !== activePool);
  const poolLabel = activePool === "console" ? "Console" : "Mobile";
  const PoolIcon = activePool === "console" ? Monitor : Smartphone;

  return (
    <div className="space-y-5 pb-8" data-testid="v2-friendlies-page">
      {/* Header */}
      <header className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#00FF87] flex items-center gap-1.5">
            <PoolIcon className="w-3 h-3" />
            // international friendlies · <span className="text-white">{poolLabel}</span>
          </div>
          <h1 className="text-2xl font-extrabold text-white mt-0.5">Friendlies</h1>
        </div>
        <Link to="/v2" className="text-[11px] font-mono text-[#71717A] hover:text-white inline-flex items-center gap-1" data-testid="v2-friendlies-back">
          <ChevronLeft className="w-3 h-3" /> back
        </Link>
      </header>

      {/* Pool switcher */}
      <div className="flex gap-2" data-testid="v2-friendlies-pool-switcher">
        {[["console", "Console", Monitor], ["mobile", "Mobile", Smartphone]].map(([p, label, Icon]) => (
          <Link
            key={p}
            to={`/v2/friendlies/${p}`}
            data-testid={`v2-friendlies-poolbtn-${p}`}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full text-[11px] font-extrabold uppercase tracking-widest transition ${
              activePool === p
                ? "bg-[#00FF87] text-[#06120A] shadow-lg"
                : "bg-white/5 text-white/70 hover:bg-white/10"
            }`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </Link>
        ))}
      </div>

      {/* Tabs */}
      <nav className="flex gap-2 -mx-1 px-1 overflow-x-auto" role="tablist">
        {[
          ["play",      "Play",      Shuffle],
          ["players",   "Players",   Users],
          ["standings", "Standings", Trophy],
          ["history",   "History",   HistoryIcon],
        ].map(([k, label, Icon]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            data-testid={`v2-friendlies-tab-${k}`}
            className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-bold uppercase tracking-widest transition-colors ${
              tab === k
                ? "bg-[#00FF87] text-[#06120A] shadow-lg"
                : "bg-white/5 text-white/70 hover:bg-white/10"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </nav>

      {/* Cross-pool warning */}
      {lockedToOtherPool && (
        <div className="rounded-2xl bg-[#1a0f00] ring-1 ring-[#FFD700]/40 p-4 text-[11px] text-white/85" data-testid="v2-fr-cross-pool-warn">
          <span className="font-bold text-[#FFD700]">Heads up:</span> You're currently revealed in the{" "}
          <b>{teamPool === "console" ? "Console" : "Mobile"}</b> pool. Play your active friendly first — then you
          can join the {poolLabel} pool.
        </div>
      )}

      {tab === "play"      && <PlayTab me={me} pool={activePool} locked={lockedToOtherPool} refresh={refresh} reloadTokens={reloadTokens} busy={busy} setBusy={setBusy} />}
      {tab === "players"   && <PlayersTab me={me} pool={activePool} refresh={refresh} />}
      {tab === "standings" && <StandingsTab me={me} pool={activePool} />}
      {tab === "history"   && <HistoryTab pool={activePool} />}

      <RulesBlock />
    </div>
  );
}

/* --------------------------------------------------------------------- */
function PlayTab({ me, pool, locked, refresh, reloadTokens, busy, setBusy }) {
  const team = me.current_team;
  const teamInThisPool = team && (team.pool || "mobile") === pool;
  const onReveal = async () => {
    if (locked) {
      toast.error("You're locked into your current team. Submit your score first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/friendlies/reveal", { pool });
      toast.success(`You revealed ${data.team.flag} ${data.team.name}!`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reveal a team.");
    } finally { setBusy(false); }
  };
  const onRedeem = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/friendlies/redeem", {});
      toast.success(`+${data.minted_tokens} tokens minted to your wallet 🎟️`);
      if (reloadTokens) reloadTokens();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Redeem failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      {/* Stats strip */}
      <div className="grid grid-cols-4 gap-2">
        <Stat label="Played" value={me.games_played} />
        <Stat label="Wins"   value={me.wins}  className="text-[#00FF87]" />
        <Stat label="Draws"  value={me.draws} />
        <Stat label="Losses" value={me.losses} className="text-[#F87171]" />
      </div>
      {/* Tokens summary */}
      <div className="rounded-2xl bg-gradient-to-r from-[#0F2A1A] to-[#0B1620] ring-1 ring-[#00FF87]/30 p-4 flex items-center justify-between" data-testid="v2-friendlies-wallet">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#00FF87]">unredeemed points</div>
          <div className="text-2xl font-extrabold text-white flex items-baseline gap-2">
            {me.points_unredeemed}
            <span className="text-[10px] font-mono text-[#71717A]">≈ {me.points_unredeemed} tokens</span>
          </div>
        </div>
        <button
          type="button"
          onClick={onRedeem}
          disabled={busy || (me.points_unredeemed || 0) <= 0}
          data-testid="v2-friendlies-redeem"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#FFD700] text-[#0F172A] text-[11px] font-extrabold uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed shadow"
        >
          <Coins className="w-3.5 h-3.5" /> Redeem
        </button>
      </div>

      {/* Reveal panel OR current team */}
      {!teamInThisPool ? (
        <button
          type="button"
          onClick={onReveal}
          disabled={busy || locked}
          data-testid="v2-friendlies-reveal-btn"
          className="w-full rounded-3xl p-6 bg-gradient-to-br from-[#0a3a25] via-[#0a4a2f] to-[#022014] ring-2 ring-[#00FF87]/50 hover:ring-[#00FF87] transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center justify-center gap-3 text-white">
            <Shuffle className="w-6 h-6 text-[#00FF87]" />
            <span className="text-base font-extrabold uppercase tracking-widest">Reveal your team</span>
          </div>
          <p className="text-[11px] text-white/70 mt-2">
            Pick a random national team from the 48-team pool. Joins the <b>{pool === "console" ? "Console" : "Mobile"}</b> friendlies pool.
          </p>
        </button>
      ) : (
        <div className="rounded-3xl p-5 bg-gradient-to-br from-[#0a3a25] via-[#082916] to-[#02100a] ring-1 ring-[#00FF87]/40" data-testid="v2-friendlies-current-team">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#00FF87]">your team</div>
            <span className="text-[9px] font-mono uppercase tracking-widest text-white/40">
              {me.can_reveal ? "ready to re-reveal" : "locked until a confirmed match"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-5xl leading-none">{team.flag}</span>
            <div className="flex-1 min-w-0">
              <div className="text-xl font-extrabold text-white truncate">{team.name}</div>
              <div className="text-[10px] font-mono text-[#71717A]">{team.confederation} · {team.code}</div>
            </div>
            {me.is_staff ? (
              <button
                type="button"
                onClick={onReveal}
                disabled={busy}
                data-testid="v2-friendlies-reveal-again"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#FFD700] text-[#0F172A] text-[10px] font-bold uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed shadow"
                title="Admin override — re-roll team"
              >
                <Shuffle className="w-3 h-3" /> Admin re-roll
              </button>
            ) : me.can_reveal ? (
              <button
                type="button"
                onClick={onReveal}
                disabled={busy}
                data-testid="v2-friendlies-reveal-again-unlocked"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#00FF87] text-[#06120A] text-[10px] font-bold uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed shadow"
                title="Reveal a new random team"
              >
                <Shuffle className="w-3 h-3" /> New team
              </button>
            ) : null}
          </div>
          <p className="text-[10px] text-white/60 mt-3">
            Tip: if your game doesn't have <b>{team.name}</b>, you may pick any other national team.
          </p>
        </div>
      )}

      {/* Active match panel(s) — tab strip when more than one is in flight */}
      {(me.active_matches?.length || 0) > 0 && (
        <ActiveMatchesSection
          matches={me.active_matches}
          viewerId={me.user.id}
          refresh={refresh}
        />
      )}

      {/* Incoming invites */}
      <InvitesPanel refresh={refresh} />
    </div>
  );
}

function Stat({ label, value, className = "" }) {
  return (
    <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-3 text-center">
      <div className={`text-xl font-extrabold ${className || "text-white"}`}>{value || 0}</div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-[#71717A] mt-0.5">{label}</div>
    </div>
  );
}

/* --------------------------------------------------------------------- */
function ActiveMatchesSection({ matches, viewerId, refresh }) {
  // Stable selected match across refreshes — fall back to the first if the
  // currently-selected one disappears (e.g. it got expired auto-cancel'd).
  const [selectedId, setSelectedId] = useState(matches[0]?.id);
  const selected = useMemo(
    () => matches.find((m) => m.id === selectedId) || matches[0],
    [matches, selectedId]
  );

  useEffect(() => {
    if (!matches.find((m) => m.id === selectedId) && matches[0]) {
      setSelectedId(matches[0].id);
    }
  }, [matches, selectedId]);

  const onSelect = async (mid) => {
    setSelectedId(mid);
    // Optimistically clear the badge on this match (the chat box will mark-read too)
    try { await api.post(`/friendlies/matches/${mid}/chat/read`); } catch { /* ignore */ }
    refresh();
  };

  const totalUnread = matches.reduce((n, m) => n + (m.chat_unread || 0), 0);

  return (
    <div className="space-y-3" data-testid="v2-friendlies-active-matches">
      {/* Tab strip (always shown, even for a single match — keeps the user
          oriented and surfaces the unread badge consistently) */}
      <div className="flex items-center gap-2 -mx-1 px-1 overflow-x-auto" role="tablist">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700] flex-shrink-0 inline-flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          your games
          {totalUnread > 0 && (
            <span
              className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-[#E11D48] text-white text-[9px] font-extrabold"
              data-testid="v2-friendlies-active-total-unread"
            >
              {totalUnread}
            </span>
          )}
        </div>
        {matches.map((m, idx) => {
          const isP1 = m.p1_id === viewerId;
          const opp  = isP1 ? m.p2_team : m.p1_team;
          const oppU = m.opp_user;
          const active = m.id === selected?.id;
          const unread = m.chat_unread || 0;
          return (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`v2-friendlies-active-tab-${m.id}`}
              onClick={() => onSelect(m.id)}
              className={`relative flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest transition ${
                active
                  ? "bg-[#FFD700] text-[#0F172A] shadow-lg"
                  : "bg-white/5 text-white/75 hover:bg-white/10"
              }`}
            >
              <span className="text-base leading-none">{opp?.flag}</span>
              <span className="truncate max-w-[100px]">@{oppU?.username || `g${idx + 1}`}</span>
              {unread > 0 && (
                <span
                  className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-[#E11D48] text-white text-[9px] font-extrabold animate-pulse"
                  data-testid={`v2-friendlies-tab-unread-${m.id}`}
                  aria-label={`${unread} unread chat message${unread === 1 ? "" : "s"}`}
                >
                  {unread}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {selected && (
        <ActiveMatchPanel
          key={selected.id}
          match={selected}
          viewerId={viewerId}
          refresh={refresh}
        />
      )}
    </div>
  );
}

/* --------------------------------------------------------------------- */
function ActiveMatchPanel({ match, viewerId, refresh }) {
  const isP1 = match.p1_id === viewerId;
  const my   = isP1 ? match.p1_team : match.p2_team;
  const opp  = isP1 ? match.p2_team : match.p1_team;
  const oppId = isP1 ? match.p2_id : match.p1_id;
  // Hydrate opponent username/avatar from /matches/:id (the /me payload only
  // returns IDs to keep it small).
  const [details, setDetails] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/friendlies/matches/${match.id}`);
        if (alive) setDetails(data);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [match.id]);
  const myUser  = isP1 ? details?.p1_user : details?.p2_user;
  const oppUser = isP1 ? details?.p2_user : details?.p1_user;

  const [my_score, setMy] = useState("");
  const [opp_score, setOpp] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  // 10-min auto-confirm window. If exactly one side has submitted, render a
  // live countdown so both players know when the unanswered score becomes
  // final automatically.
  const singleAt = details?.single_submitted_at || match.single_submitted_at;
  const windowSecs = details?.auto_confirm_window_secs || match.auto_confirm_window_secs || 600;
  const bothSubmitted = !!(details?.p1_submission && details?.p2_submission);
  const someoneSubmitted = !!(details?.p1_submission || details?.p2_submission);
  const mySubmitted = isP1 ? !!details?.p1_submission : !!details?.p2_submission;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!singleAt || bothSubmitted) return;
    const tk = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tk);
  }, [singleAt, bothSubmitted]);
  const secsLeft = singleAt && !bothSubmitted
    ? Math.max(0, Math.floor((new Date(singleAt).getTime() + windowSecs * 1000 - now) / 1000))
    : null;
  const mm = secsLeft != null ? String(Math.floor(secsLeft / 60)).padStart(2, "0") : "00";
  const ss = secsLeft != null ? String(secsLeft % 60).padStart(2, "0") : "00";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (my_score === "" || opp_score === "" || !file) {
      toast.error("Enter both scores and attach a screenshot.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("my_score", String(parseInt(my_score, 10)));
      fd.append("opp_score", String(parseInt(opp_score, 10)));
      fd.append("screenshot", file);
      await api.post(`/friendlies/matches/${match.id}/submit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Score submitted!");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-3xl bg-gradient-to-br from-[#1a0f00] to-[#06120A] ring-2 ring-[#FFD700]/60 p-5 space-y-3" data-testid="v2-friendlies-active-match">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-[#FFD700]" />
        <div className="text-[11px] font-bold uppercase tracking-widest text-[#FFD700]">Active friendly — submit your score</div>
      </div>
      <div className="flex items-center justify-around text-center text-white">
        <div className="min-w-0 max-w-[40%]">
          <div className="text-3xl">{my?.flag}</div>
          <div className="text-[10px] font-mono text-white/70 mt-1">{my?.code}</div>
          <div className="text-[9px] text-[#71717A] mt-0.5">you</div>
          <div className="text-[10px] font-bold text-white/90 mt-1 truncate" data-testid="v2-fr-active-myname">
            @{myUser?.username || "you"}
          </div>
        </div>
        <div className="text-[10px] font-mono text-[#71717A]">vs</div>
        <div className="min-w-0 max-w-[40%]">
          <div className="text-3xl">{opp?.flag}</div>
          <div className="text-[10px] font-mono text-white/70 mt-1">{opp?.code}</div>
          <Link to={`/v2/players/${oppId}`} className="text-[9px] text-[#00FF87] underline">opponent</Link>
          <div className="text-[10px] font-bold text-white/90 mt-1 truncate" data-testid="v2-fr-active-oppname">
            @{oppUser?.username || oppId?.slice(0,6)}
          </div>
        </div>
      </div>
      <form className="space-y-2 pt-1" onSubmit={onSubmit}>
        <div className="grid grid-cols-2 gap-2">
          <ScoreInput label="Your score"     value={my_score}  setValue={setMy}  testid="v2-fr-score-mine" />
          <ScoreInput label="Opponent score" value={opp_score} setValue={setOpp} testid="v2-fr-score-opp" />
        </div>
        <label className="flex items-center gap-2 rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2.5 cursor-pointer">
          <Upload className="w-4 h-4 text-[#71717A]" />
          <span className="text-[11px] text-white/80 flex-1 truncate">
            {file ? file.name : "Attach screenshot (required)"}
          </span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="v2-fr-screenshot"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </label>
        <button
          type="submit"
          disabled={busy}
          data-testid="v2-friendlies-submit"
          className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-[#FFD700] text-[#0F172A] text-[11px] font-extrabold uppercase tracking-widest shadow disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Submit my score
        </button>
        <p className="text-[10px] text-white/55 leading-snug" data-testid="v2-fr-submit-note">
          Once you submit your score, any other pending invites or accepted matches you have with this team are auto-cancelled. You'll be able to reveal a new team as soon as <b>both</b> scores match and the result is confirmed.
        </p>

        {/* 10-minute auto-confirm disclaimer — always visible above the
            submit button so both players see the rule before they play. */}
        <div
          className={`rounded-xl border p-2.5 text-[10.5px] leading-snug ${
            someoneSubmitted && !bothSubmitted
              ? "border-amber-400/50 bg-amber-500/10 text-amber-100"
              : "border-[#FFD700]/30 bg-[#FFD700]/5 text-[#FFE99A]"
          }`}
          data-testid="v2-fr-auto-confirm-disclaimer"
        >
          <div className="flex items-start gap-2">
            <Clock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold uppercase tracking-widest text-[9.5px] mb-0.5">10-min auto-confirm rule</div>
              <div>
                Once one player submits their score, the other has <b>10 minutes</b> to submit theirs.
                If the second submission doesn't arrive in time, the first player's score is taken as <b>final</b>.
                Submit promptly to dispute or correct anything.
              </div>
              {someoneSubmitted && !bothSubmitted && secsLeft != null && (
                <div
                  className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/40 font-mono tabular-nums text-[11px] font-bold"
                  data-testid="v2-fr-auto-confirm-countdown"
                >
                  {mySubmitted
                    ? <>Opponent has <span className="text-amber-300">{mm}:{ss}</span> to submit</>
                    : <>You have <span className="text-amber-300">{mm}:{ss}</span> to submit</>}
                </div>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* Pair chat (host ↔ guest) */}
      <FriendlyChatBox matchId={match.id} viewerId={viewerId} oppUsername={oppUser?.username} />
    </div>
  );
}

/* --------------------------------------------------------------------- */
function FriendlyChatBox({ matchId, viewerId, oppUsername }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = React.useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/friendlies/matches/${matchId}/chat`);
      setMessages(data.messages || []);
      // Mark this match as read whenever the chat box has loaded — the
      // user is actively looking at this conversation. Best-effort.
      try { await api.post(`/friendlies/matches/${matchId}/chat/read`); } catch { /* ignore */ }
    } catch { /* ignore */ }
  }, [matchId]);

  useEffect(() => { load(); }, [load]);
  // Light polling every 7s so opponent messages show up reasonably fast.
  useEffect(() => {
    const t = setInterval(load, 7000);
    return () => clearInterval(t);
  }, [load]);
  // Auto-scroll to the latest message
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const sendText = async (content) => {
    if (!content || !content.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/friendlies/matches/${matchId}/chat`, { content: content.trim() });
      setMessages((m) => [...m, data.message]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send message.");
      throw err;
    } finally { setSending(false); }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    if (!text.trim()) return;
    try {
      await sendText(text);
      setText("");
    } catch { /* error already toasted */ }
  };

  return (
    <div className="mt-2 rounded-2xl bg-[#06120A]/70 ring-1 ring-white/10 overflow-hidden" data-testid="v2-friendlies-chat">
      <div className="px-3 py-2 flex items-center justify-between border-b border-white/10">
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#00FF87] inline-flex items-center gap-1.5">
          <Send className="w-3 h-3" /> chat with @{oppUsername || "opponent"}
        </span>
        <span className="text-[9px] font-mono text-[#71717A]">{messages.length} msg</span>
      </div>
      <div ref={listRef} className="max-h-[180px] min-h-[60px] overflow-y-auto px-3 py-2 space-y-1.5" data-testid="v2-friendlies-chat-list">
        {messages.length === 0 ? (
          <div className="text-[10px] text-[#71717A] italic text-center py-3">
            Say hi to your opponent — agree on half length or just trash-talk.
          </div>
        ) : messages.map((m) => {
          const mine = m.user_id === viewerId;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`v2-fr-chat-msg-${m.id}`}>
              <div className={`max-w-[78%] px-2.5 py-1.5 rounded-2xl text-[11.5px] leading-snug break-words shadow ${
                mine ? "bg-[#00FF87] text-[#06120A] rounded-br-md" : "bg-white/10 text-white rounded-bl-md"
              }`}>
                {!mine && (
                  <div className="text-[8.5px] font-mono uppercase tracking-widest text-[#00FF87]/80 mb-0.5">
                    @{m.username || m.user_id?.slice(0,6)}
                  </div>
                )}
                {m.content}
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="border-t border-white/10 p-2 space-y-2">
        <QuickReplyChips
          onSend={sendText}
          disabled={sending}
          testIdPrefix="v2-friendlies-chat-quick"
        />
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type a message…"
            maxLength={1000}
            data-testid="v2-friendlies-chat-input"
            /* font-size MUST be ≥ 16px to disable iOS Safari's auto-zoom-on-focus
               which otherwise makes the PWA feel like a web page. */
            style={{ fontSize: "16px" }}
            className="flex-1 bg-white/5 ring-1 ring-white/10 rounded-full px-3 py-2 text-white placeholder:text-[#71717A] focus:ring-[#00FF87] outline-none"
          />
          <button
            type="submit"
            disabled={sending || !text.trim()}
            data-testid="v2-friendlies-chat-send"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#00FF87] text-[#06120A] disabled:opacity-40 disabled:cursor-not-allowed shadow"
            aria-label="Send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </form>
    </div>
  );
}

function ScoreInput({ label, value, setValue, testid }) {
  return (
    <label className="block">
      <span className="block text-[9px] font-mono uppercase tracking-widest text-[#71717A] mb-1">{label}</span>
      <input
        type="number" min="0" max="99" inputMode="numeric" pattern="[0-9]*"
        value={value} onChange={(e) => setValue(e.target.value)}
        data-testid={testid}
        /* Explicit 16px+ font-size locks iOS out of the focus auto-zoom. */
        style={{ fontSize: "26px" }}
        className="w-full rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2.5 text-center font-extrabold text-white focus:ring-[#00FF87] outline-none"
      />
    </label>
  );
}

/* --------------------------------------------------------------------- */
function InvitesPanel({ refresh }) {
  const [data, setData] = useState({ incoming: [], outgoing: [] });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/friendlies/invites");
      setData(data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const respond = async (id, action) => {
    setBusy(true);
    try {
      await api.post(`/friendlies/invites/${id}/${action}`);
      toast.success(action === "accept" ? "Invite accepted!" : `Invite ${action}d`);
      await load();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't ${action}.`);
    } finally { setBusy(false); }
  };

  if (data.incoming.length === 0 && data.outgoing.length === 0) return null;

  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 space-y-3" data-testid="v2-friendlies-invites">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">Invitations</div>
      {data.incoming.map((inv) => (
        <div key={inv.id} className="flex items-center gap-3" data-testid={`v2-fr-invite-incoming-${inv.id}`}>
          <span className="text-2xl">{inv.from_team?.flag}</span>
          <div className="flex-1 min-w-0 text-white">
            <div className="text-sm font-bold truncate">@{inv.from_user?.username || inv.from_id?.slice(0, 6)}</div>
            <div className="text-[10px] text-[#71717A]">wants to play with <b>{inv.from_team?.name}</b> vs your <b>{inv.to_team?.name}</b></div>
          </div>
          <button type="button" disabled={busy} onClick={() => respond(inv.id, "accept")}
            className="px-2.5 py-1.5 rounded-full bg-[#00FF87] text-[#06120A] text-[10px] font-extrabold uppercase tracking-widest disabled:opacity-40"
            data-testid={`v2-fr-invite-accept-${inv.id}`}>Accept</button>
          <button type="button" disabled={busy} onClick={() => respond(inv.id, "decline")}
            className="px-2.5 py-1.5 rounded-full bg-white/10 text-white text-[10px] font-bold uppercase tracking-widest disabled:opacity-40"
            data-testid={`v2-fr-invite-decline-${inv.id}`}>Decline</button>
        </div>
      ))}
      {data.outgoing.map((inv) => (
        <div key={inv.id} className="flex items-center gap-3" data-testid={`v2-fr-invite-outgoing-${inv.id}`}>
          <span className="text-2xl opacity-70">{inv.to_team?.flag}</span>
          <div className="flex-1 min-w-0 text-white/80">
            <div className="text-sm truncate">@{inv.to_user?.username || inv.to_id?.slice(0, 6)}</div>
            <div className="text-[10px] text-[#71717A]">awaiting acceptance · {inv.to_team?.name}</div>
          </div>
          <button type="button" disabled={busy} onClick={() => respond(inv.id, "cancel")}
            className="px-2.5 py-1.5 rounded-full bg-white/5 text-white/70 text-[10px] font-bold uppercase tracking-widest disabled:opacity-40"
            data-testid={`v2-fr-invite-cancel-${inv.id}`}>Cancel</button>
        </div>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------------- */
function PlayersTab({ me, pool, refresh }) {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/friendlies/players", { params: { platform: pool } });
      setList(data.players || []);
    } catch { setList([]); }
  }, [pool]);
  useEffect(() => { load(); }, [load]);

  const invite = async (uid) => {
    if (!me.current_team || (me.current_team.pool && me.current_team.pool !== pool)) {
      toast.error(`Reveal your team in the ${pool === "console" ? "Console" : "Mobile"} pool first.`);
      return;
    }
    setBusy((b) => ({ ...b, [uid]: true }));
    try {
      await api.post("/friendlies/invite", { to_user_id: uid });
      toast.success("Invite sent!");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send invite.");
    } finally {
      setBusy((b) => ({ ...b, [uid]: false }));
    }
  };

  if (!list) return <CenterLoader />;
  if (list.length === 0) return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6 text-center">
      <Users className="w-6 h-6 text-[#71717A] mx-auto mb-2" />
      <div className="text-sm text-white/80">No one else has revealed a team yet.</div>
      <div className="text-[11px] text-[#71717A] mt-1">Be the first — come back in a bit.</div>
    </div>
  );

  return (
    <div className="space-y-2" data-testid="v2-friendlies-players">
      {list.map((p) => (
        <div key={p.id} className="flex items-center gap-3 rounded-2xl bg-white/5 ring-1 ring-white/10 p-3" data-testid={`v2-fr-player-${p.id}`}>
          <span className="text-3xl">{p.current_team?.flag}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-white truncate">@{p.username}</div>
            <div className="text-[10px] text-[#71717A]">
              {p.current_team?.name} · {p.games_played} played · {p.wins}W
            </div>
          </div>
          <button
            type="button"
            disabled={!!busy[p.id]}
            onClick={() => invite(p.id)}
            data-testid={`v2-fr-invite-${p.id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#00FF87] text-[#06120A] text-[10px] font-extrabold uppercase tracking-widest disabled:opacity-40"
          >
            {busy[p.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Invite
          </button>
        </div>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------------- */
function StandingsTab({ me, pool }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/friendlies/standings", { params: { platform: pool } });
        setRows(data.standings || []);
      } catch { setRows([]); }
    })();
  }, [pool]);
  if (!rows) return <CenterLoader />;
  if (rows.length === 0) return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6 text-center text-sm text-white/70">
      No matches played yet — be the first to score!
    </div>
  );
  return (
    <div className="rounded-2xl overflow-hidden ring-1 ring-white/10" data-testid="v2-friendlies-standings">
      <div className="grid grid-cols-12 gap-1 px-3 py-2 text-[9px] font-mono uppercase tracking-widest text-[#71717A] bg-white/5">
        <div className="col-span-1">#</div>
        <div className="col-span-5">Player</div>
        <div className="col-span-1 text-center">P</div>
        <div className="col-span-1 text-center">W</div>
        <div className="col-span-1 text-center">D</div>
        <div className="col-span-1 text-center">L</div>
        <div className="col-span-2 text-right">Pts</div>
      </div>
      {rows.map((r) => {
        const isMe = r.id === me.user.id;
        return (
          <div
            key={r.id}
            className={`grid grid-cols-12 gap-1 px-3 py-2 text-[12px] items-center border-t border-white/5 ${isMe ? "bg-[#00FF87]/10" : ""}`}
            data-testid={`v2-fr-standings-row-${r.id}`}
          >
            <div className={`col-span-1 font-mono ${r.rank === 1 ? "text-[#FFD700]" : "text-white/60"}`}>{r.rank}</div>
            <div className="col-span-5 flex items-center gap-1.5 min-w-0">
              <span className="text-base shrink-0">{r.current_team?.flag || "🏳️"}</span>
              <span className={`font-bold truncate ${isMe ? "text-[#00FF87]" : "text-white"}`}>@{r.username}{isMe && " ◂ you"}</span>
            </div>
            <div className="col-span-1 text-center text-white/80">{r.games_played}</div>
            <div className="col-span-1 text-center text-[#00FF87]">{r.wins}</div>
            <div className="col-span-1 text-center text-white/70">{r.draws}</div>
            <div className="col-span-1 text-center text-[#F87171]">{r.losses}</div>
            <div className="col-span-2 text-right font-extrabold text-white">{r.points_total}</div>
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------------------- */
function HistoryTab({ pool }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/friendlies/history");
        // Filter to the active pool client-side
        const all = data.history || [];
        setRows(all.filter((m) => (m.platform || "mobile") === pool));
      } catch { setRows([]); }
    })();
  }, [pool]);
  if (!rows) return <CenterLoader />;
  if (rows.length === 0) return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6 text-center text-sm text-white/70">
      No completed friendlies yet.
    </div>
  );
  return (
    <div className="space-y-2" data-testid="v2-friendlies-history">
      {rows.map((m) => {
        const tone =
          m.outcome === "win"   ? "text-[#00FF87]" :
          m.outcome === "loss"  ? "text-[#F87171]" :
          "text-white/70";
        return (
          <div key={m.id} className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-3 flex items-center gap-3" data-testid={`v2-fr-history-${m.id}`}>
            <span className="text-2xl">{m.my_team?.flag}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-white truncate">{m.my_team?.name} vs {m.opp_team?.name}</div>
              <div className="text-[10px] text-[#71717A]">
                @{m.opp_user?.username || m.opp_id?.slice(0,6)} · {m.confirmed_at?.slice(0,10) || ""}
              </div>
            </div>
            <div className={`text-base font-extrabold tabular-nums ${tone}`}>{m.my_score} – {m.opp_score}</div>
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------------------- */
function RulesBlock() {
  return (
    <details className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4" data-testid="v2-friendlies-rules">
      <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-widest text-[#71717A] flex items-center gap-2">
        <Info className="w-3.5 h-3.5" />
        Rules & disclaimers
      </summary>
      <ul className="mt-3 space-y-1.5 text-[11px] text-white/80 leading-relaxed list-disc list-inside">
        <li><b>No extra time</b> — full-time score is final, even if it's a draw.</li>
        <li><b>Players' condition: random</b> — set sliders to random in your game settings.</li>
        <li><b>Half-length</b>: agreed before kickoff (default 6 minutes).</li>
        <li>If your game doesn't include the team you drew, you may pick <b>any other national team</b>.</li>
        <li><b>Win = 3 pts · Draw = 1 pt · Loss = 0 pts</b>. Standings ranked by total points.</li>
        <li>Points convert <b>1:1 to tokens</b> (so a win mints 3, a draw mints 1).</li>
        <li>You can <b>send invites to multiple players at once</b> — first one to play with you gets the match. Avoids being stuck if someone ghosts.</li>
        <li>Multiple players can accept your invite; you'll have one active match at a time. When you submit your score, any other pending invites and accepted matches you have with this team auto-cancel.</li>
        <li>Once you reveal a team, it's <b>locked</b> — you can only get a new one after at least one match with this team is <b>confirmed</b> (both scores match). Only admins can force a re-roll.</li>
        <li>Disputed scores go to admin override; once resolved, points are credited and you can re-reveal.</li>
      </ul>
    </details>
  );
}

function CenterLoader() {
  return <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#00FF87]" /></div>;
}
